/* ============================================================
   Local durability for the store — the disk half of offline-first.

   Three keys per signed-in user, plus one key belonging to the DEVICE:
     • state   a full AppState snapshot, so a cold launch paints the last known
               data instead of an empty app while the network is unreachable.
     • outbox  the queue of mutations that haven't been acknowledged by the
               server yet (see store/outbox.ts), so a tap made offline survives
               a force-quit instead of being dropped.
     • prefs   the user's settings plus any change to them the server hasn't
               acknowledged. Preferences live above the store in the tree (the
               store reads `weekStartsOn` from them), so they can't share its
               outbox — they carry their own coalescing patch instead. See
               PreferencesContext.

   The fourth, `view`, is deliberately NOT keyed by user — see below.

   Keyed by the Cognito `sub` so two accounts on one device never see each
   other's rows, and NOT cleared on sign-out — signing back into the same
   account should warm-start (the cache is the same trade Things/Todoist make).
   Account deletion is what must purge it; see clearPersisted.

   Storage is whatever KeyValueStorage the platform hands ClientCoreProvider —
   @react-native-async-storage/async-storage on native (rather than
   expo-sqlite/kv-store: the app ships web as well, and expo-sqlite's web
   backend is alpha and needs Metro wasm config plus COEP/COOP headers for
   SharedArrayBuffer; AsyncStorage is stable on all three and one JSON blob per
   key is all this layer needs. Its ceiling is a per-key size limit on Android,
   a few MB; if snapshots ever approach it, the move is to expo-sqlite/kv-store
   or a row-per-entity table, not a bigger blob) and localStorage/IndexedDB on
   the web. `createPersist` binds that store once; `usePersist()` hands the
   bound functions to the providers.

   Nothing here throws. A corrupt or unreadable cache must degrade to "no cache"
   — never to a crash on launch — and a failed write must not break a mutation
   the user already saw succeed on screen.
   ============================================================ */
import { isPlan, isPlanReason, isPlanStore, type Plan, type PlanReason, type PlanStore } from '@habishine/shared';

import type { KeyValueStorage } from '../adapters';
import type { ServerPreferences } from '../api/types';
import type { AppState } from '../types';
import type { OutboxEntry } from './outbox';

/** Envelope version for the OUTBOX and PREFS keys. Bump when OutboxOp or the
   prefs cache change shape incompatibly — a mismatch discards them rather than
   feeding the app rows it can't read. */
const SCHEMA_VERSION = 2;

/** The SNAPSHOT's version, deliberately its own constant — the mirror image of
   why `VIEW_SCHEMA_VERSION` is separate below.

   The snapshot is a pure cache: the server holds the real copy, so discarding it
   costs exactly one cold sync. The OUTBOX sharing that number is what made a
   bump expensive, because the outbox is NOT a cache — it holds writes the server
   has never seen, and dropping it throws away work a user did offline (the same
   reasoning `migrateRenamedOp` spells out below, which is why that rename
   avoided a bump and leaned on `isAppState` instead).

   Splitting them makes the cheap fix cheap again: a change to the shape of a ROW
   is one bump here, and nobody's queued writes pay for it.

   3 — tags gained `hue`, and tasks/habits gained `sortOrder`, none of which
   `isAppState` can see (it is shallow about rows on purpose). A pre-hue snapshot
   therefore survived, and `NavSidebar` read `hues[tag.hue]` on a tag that had
   none — `undefined.c`, a JS fatal on launch, for upgraders only. */
const STATE_SCHEMA_VERSION = 3;

/** The view key's version, deliberately its own constant.

   Discarding a cache is cheap for the account keys because the server holds the
   real copy: state re-syncs, prefs re-fetch, and the outbox is transient by
   design. **The view key has no server copy** — it's device-local, which is the
   whole point of it — so a discard there is permanent data loss, small but
   real: the user's habit layout silently snaps back to the default.

   Sharing SCHEMA_VERSION would tie that loss to a completely unrelated event —
   the next time AppState or OutboxOp changes shape, every user's layout choice
   would reset for no reason a user could ever perceive. So it versions on its
   own, and only a change to the ViewPrefs shape itself may bump it. */
const VIEW_SCHEMA_VERSION = 1;

interface Envelope<T> {
  v: number;
  data: T;
}

const stateKey = (userKey: string) => `dt:${userKey}:state`;
const outboxKey = (userKey: string) => `dt:${userKey}:outbox`;
const prefsKey = (userKey: string) => `dt:${userKey}:prefs`;
/** Entitlement, cached so a cold launch with no signal knows whether to show the
   app or the paywall. Its OWN key rather than a field on the snapshot: the
   snapshot is dropped wholesale on a schema bump (it is a pure cache and the
   next sync refills it), and dropping the plan would wall a paying subscriber
   out of an app they are entitled to until they get signal. Same reasoning that
   keeps the outbox separate, for a different reason. */
const planKey = (userKey: string) => `dt:${userKey}:plan`;
/** View preferences belong to the SCREEN, not the account, so this key carries
   no user segment: it's the same answer for whoever is signed in on this
   device, and it survives a sign-out that changes nothing about the display. */
const VIEW_KEY = 'dt:view';

/** Every preference field the client may write. */
export type PrefFields = Pick<
  ServerPreferences,
  | 'themePref'
  | 'weekStartsOn'
  | 'remindersEnabled'
  | 'productNewsEnabled'
  | 'morningSummaryTime'
  | 'eveningReviewTime'
  | 'timezone'
  | 'quietHours'
>;

/** The values worth caching. `timezone` is excluded deliberately: this device
   re-detects its own zone every launch and is the authority on it, so a stored
   copy could only ever be staler than the live one. */
export type PrefValues = Omit<PrefFields, 'timezone'>;

/** Preference changes the server hasn't acknowledged, coalesced by field —
   setting the same field twice offline leaves one value to send, the last. */
export type PrefPatch = Partial<PrefFields>;

/** Both halves under one key, so they can't tear apart across a crash. */
export interface PrefsCache {
  values: PrefValues;
  pending: PrefPatch;
}

/** Read + parse an envelope, or null when absent, unreadable, or stamped with a
   version other than `version` (default: the account keys' SCHEMA_VERSION). */
async function readEnvelope<T>(
  storage: KeyValueStorage,
  key: string,
  version = SCHEMA_VERSION,
): Promise<T | null> {
  let raw: string | null;
  try {
    raw = await storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Envelope<T>;
    if (!parsed || parsed.v !== version) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

async function writeEnvelope<T>(
  storage: KeyValueStorage,
  key: string,
  data: T,
  version = SCHEMA_VERSION,
): Promise<void> {
  try {
    await storage.setItem(key, JSON.stringify({ v: version, data }));
  } catch {
    // Out of space / storage unavailable. The in-memory state is unaffected and
    // the next write retries; losing the cache only costs a cold sync.
  }
}

/** Structural check on a parsed snapshot. Shallow about ROWS — it doesn't
   pretend to validate every task or habit, since the server is the authority
   and the background sync corrects anything stale — but exhaustive about the
   TOP-LEVEL FIELDS: every required key of AppState must be checked here.

   That exhaustiveness is what makes an older app version's snapshot fail
   closed. A snapshot is adopted verbatim (`update(() => cached)` in
   store/api.tsx), so a key this check skips arrives as `undefined` and the
   first selector to touch it throws during the initial render — a JS fatal,
   i.e. a crash on launch for upgraders only, invisible to a fresh install.
   That is exactly what shipping `projects` past a check that predated it did.

   So: ADD A LINE HERE whenever AppState gains a required field.

   It is also what retires a snapshot across the lists → projects rename: the old
   shape has `lists` and no `projects`, so it fails here and the next sync refills
   it. (The OUTBOX is migrated rather than dropped — see `migrateRenamedOp`.)

   Note this check alone does NOT cover a new required field on a ROW — an
   upgrader's cached tasks would still arrive without `tagIds` and throw on the
   first `.map`. A row-shape change is `STATE_SCHEMA_VERSION`'s job instead: it
   drops the whole stale snapshot, so no old row shape survives the upgrade.

   So: ADD A LINE HERE for a new top-level field, and BUMP STATE_SCHEMA_VERSION
   for a new required field on a row. Tags shipped their `hue` without either,
   which is how a hueless tag reached `hues[tag.hue]` in NavSidebar and took the
   app down on launch. */
function isAppState(x: unknown): x is AppState {
  if (!x || typeof x !== 'object') return false;
  const s = x as Partial<AppState>;
  return (
    typeof s.cap === 'number' &&
    Array.isArray(s.tasks) &&
    Array.isArray(s.habits) &&
    Array.isArray(s.projects) &&
    Array.isArray(s.tags) &&
    !!s.pinnedByDay &&
    typeof s.pinnedByDay === 'object' &&
    !!s.checkins &&
    typeof s.checkins === 'object'
  );
}

function isOutboxEntry(x: unknown): x is OutboxEntry {
  if (!x || typeof x !== 'object') return false;
  const e = x as Partial<OutboxEntry>;
  return (
    typeof e.seq === 'number' &&
    typeof e.tries === 'number' &&
    !!e.op &&
    typeof e.op === 'object' &&
    typeof (e.op as { kind?: unknown }).kind === 'string'
  );
}

/** Shallow check on a parsed prefs cache. The individual fields are validated by
   PreferencesContext's `adopt`, which applies the same guards to a cached value
   as to a server one — so a garbage `themePref` on disk can't reach the theme. */
function isPrefsCache(x: unknown): x is PrefsCache {
  if (!x || typeof x !== 'object') return false;
  const c = x as Partial<PrefsCache>;
  return (
    !!c.values && typeof c.values === 'object' && !!c.pending && typeof c.pending === 'object'
  );
}


/**
 * Rewrite a queued op written before "list" was renamed to "project".
 *
 * This runs instead of a SCHEMA_VERSION bump ON PURPOSE. Bumping the version
 * drops the whole envelope, and for the SNAPSHOT that's exactly right (it's a
 * cache; the next sync refills it). The OUTBOX is not a cache — it holds writes
 * the server has never seen, and an upgrade that lands while a user is offline
 * would silently throw their work away.
 *
 * So the snapshot is still dropped, by `isAppState` failing on the missing
 * `projects` key, while the queue is carried across: op kinds `list.*` become
 * `project.*`, and the `listId` a queued task create or patch carries becomes
 * `projectId`, which is what the renamed API now expects.
 *
 * Deletable once no device can still be holding a pre-rename queue — realistically
 * one release after everyone has opened the app online at least once.
 */
function migrateRenamedOp(op: Record<string, unknown>): Record<string, unknown> {
  const next = { ...op };
  const kind = next.kind;
  if (typeof kind === 'string' && kind.startsWith('list.')) {
    next.kind = `project.${kind.slice('list.'.length)}`;
  }
  for (const field of ['body', 'patch'] as const) {
    const inner = next[field];
    if (inner && typeof inner === 'object' && 'listId' in inner) {
      const { listId, ...rest } = inner as Record<string, unknown>;
      next[field] = { ...rest, projectId: listId };
    }
  }
  return next;
}


/* ---------- entitlement ---------- */

/** The last plan the server told us about, and WHEN it told us.

   `confirmedAt` is the load-bearing half. Without it the cache is a value with
   no age, and the app cannot tell "confirmed Pro five minutes ago" from
   "confirmed Pro last March" — which is the difference between trusting it and
   not (docs/subscriptions.md §5, the 7-day offline grace). */
export interface PlanCache {
  plan: Plan;
  /** ISO timestamp of the sync that returned this plan. */
  confirmedAt: string;
  /** The server's reason for the plan (docs/subscriptions.md §7c), so an offline
     cold start still words a transferred-away account correctly. Optional:
     caches written before the field existed are still valid. */
  reason?: PlanReason | null;
  /** Which store bills the plan, so an offline Settings screen still points
     "manage subscription" at the right place. Optional for the same reason. */
  store?: PlanStore | null;
}

function isPlanCache(x: unknown): x is PlanCache {
  if (!x || typeof x !== 'object') return false;
  const c = x as Partial<PlanCache>;
  const reasonOk = c.reason === undefined || c.reason === null || isPlanReason(c.reason);
  const storeOk = c.store === undefined || c.store === null || isPlanStore(c.store);
  return isPlan(c.plan) && typeof c.confirmedAt === 'string' && reasonOk && storeOk;
}


/** How the habit list is laid out on Today — two-up tiles or single rows.
   Device-local (see ViewPrefsContext for why this isn't a synced preference). */
export type HabitLayout = 'grid' | 'list';

export interface ViewPrefs {
  habitLayout: HabitLayout;
  /** whether this device shows today as a Live Activity on the Lock Screen.
     Device-local for the same reason `habitLayout` is, and more so: a Lock
     Screen card belongs to the phone in your pocket, not to the account. */
  lockScreen: boolean;
}

/** Accepts a record written before a field existed and fills the default in,
   rather than rejecting it. This is why adding to ViewPrefs must NOT bump
   `VIEW_SCHEMA_VERSION`: this key has no server copy, so a version bump
   discards it permanently and would silently reset every user's habit layout
   for a reason no user could perceive. Widen the guard instead. */
type StoredViewPrefs = Partial<ViewPrefs> & Pick<ViewPrefs, 'habitLayout'>;

function isViewPrefs(x: unknown): x is StoredViewPrefs {
  if (!x || typeof x !== 'object') return false;
  const v = x as Partial<ViewPrefs>;
  return v.habitLayout === 'grid' || v.habitLayout === 'list';
}

/** Read a stored record into a complete one, defaulting anything absent. */
export function withViewPrefDefaults(stored: StoredViewPrefs): ViewPrefs {
  return { habitLayout: stored.habitLayout, lockScreen: stored.lockScreen ?? true };
}

/** The disk layer, bound to one platform's storage. One per app, built by
   ClientCoreProvider; reached through `usePersist()` in React and held as a
   plain object elsewhere. */
export interface Persist {
  loadState(userKey: string): Promise<AppState | null>;
  saveState(userKey: string, state: AppState): Promise<void>;
  /** Load the pending queue. Any entry that doesn't parse is dropped rather than
     failing the whole load — one bad row shouldn't cost the user every other
     pending write. */
  loadOutbox(userKey: string): Promise<OutboxEntry[]>;
  saveOutbox(userKey: string, entries: OutboxEntry[]): Promise<void>;
  loadPrefs(userKey: string): Promise<PrefsCache | null>;
  savePrefs(userKey: string, cache: PrefsCache): Promise<void>;
  loadPlanCache(userKey: string): Promise<PlanCache | null>;
  savePlanCache(userKey: string, cache: PlanCache): Promise<void>;
  loadViewPrefs(): Promise<ViewPrefs | null>;
  saveViewPrefs(prefs: ViewPrefs): Promise<void>;
  /** Drop everything cached for a user. For account deletion (and any future
     "clear local data") — sign-out deliberately keeps the cache. The device's
     view preferences are untouched: they're not this user's data, and wiping
     them would silently reset the display for whoever signs in next. */
  clearPersisted(userKey: string): Promise<void>;
}

export function createPersist(storage: KeyValueStorage): Persist {
  return {
    async loadState(userKey) {
      const data = await readEnvelope<unknown>(storage, stateKey(userKey), STATE_SCHEMA_VERSION);
      return isAppState(data) ? data : null;
    },
    saveState(userKey, state) {
      return writeEnvelope(storage, stateKey(userKey), state, STATE_SCHEMA_VERSION);
    },
    async loadOutbox(userKey) {
      const data = await readEnvelope<unknown>(storage, outboxKey(userKey));
      if (!Array.isArray(data)) return [];
      return data
        .map((e) =>
          e && typeof e === 'object' && (e as { op?: unknown }).op &&
          typeof (e as { op: unknown }).op === 'object'
            ? { ...(e as object), op: migrateRenamedOp((e as { op: Record<string, unknown> }).op) }
            : e,
        )
        .filter(isOutboxEntry);
    },
    saveOutbox(userKey, entries) {
      return writeEnvelope(storage, outboxKey(userKey), entries);
    },
    async loadPrefs(userKey) {
      const data = await readEnvelope<unknown>(storage, prefsKey(userKey));
      return isPrefsCache(data) ? data : null;
    },
    savePrefs(userKey, cache) {
      return writeEnvelope(storage, prefsKey(userKey), cache);
    },
    async loadPlanCache(userKey) {
      const data = await readEnvelope<unknown>(storage, planKey(userKey));
      return isPlanCache(data) ? data : null;
    },
    savePlanCache(userKey, cache) {
      return writeEnvelope(storage, planKey(userKey), cache);
    },
    async loadViewPrefs() {
      const data = await readEnvelope<unknown>(storage, VIEW_KEY, VIEW_SCHEMA_VERSION);
      return isViewPrefs(data) ? withViewPrefDefaults(data) : null;
    },
    saveViewPrefs(prefs) {
      return writeEnvelope(storage, VIEW_KEY, prefs, VIEW_SCHEMA_VERSION);
    },
    async clearPersisted(userKey) {
      try {
        await Promise.all(
          [stateKey(userKey), outboxKey(userKey), prefsKey(userKey), planKey(userKey)].map((k) =>
            storage.removeItem(k),
          ),
        );
      } catch {
        // nothing else to try; the keys are namespaced so stale data is inert
      }
    },
  };
}
